import { useState, useEffect, useCallback, useRef } from 'react';
import type { FormEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Upload, Pencil, Trash2, Search, Check, X } from 'lucide-react';
import {
  getCatalog as fetchCatalog,
  listProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  bulkCreateProducts,
  batchDeleteProducts,
  getProductGroups,
  ApiError,
} from '../api/client.js';
import type { Product, Catalog } from '../api/client.js';

function formatCurrency(value: string | number): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

interface ParsedRow {
  groupName?: string;
  partNumber: string;
  description?: string;
  msrp: number;
  applyProjectDiscounts: boolean;
  error?: string;
}

function parseBooleanValue(value: string | undefined): boolean {
  if (!value) return true;
  const v = value.trim().toLowerCase();
  if (['false', 'no', 'n', '0'].includes(v)) return false;
  return true;
}

function parseImportData(text: string): { rows: ParsedRow[]; errors: string[] } {
  const lines = text.trim().split('\n').filter((l) => l.trim());
  if (lines.length === 0) return { rows: [], errors: ['No data to import'] };

  // Detect delimiter
  const firstLine = lines[0]!;
  const delimiter = firstLine.includes('\t') ? '\t' : ',';

  // Check for header row
  let startIndex = 0;
  const headerCheck = firstLine.toLowerCase();
  if (
    headerCheck.includes('part') ||
    headerCheck.includes('number') ||
    headerCheck.includes('description') ||
    headerCheck.includes('msrp')
  ) {
    startIndex = 1;
  }

  const rows: ParsedRow[] = [];
  const errors: string[] = [];

  for (let i = startIndex; i < lines.length; i++) {
    const line = lines[i]!;
    const cols = line.split(delimiter).map((c) => c.trim());
    const rowNum = i + 1;

    const partNumber = cols[1] ?? '';
    if (!partNumber) {
      errors.push(`Row ${rowNum}: Part number is required`);
      continue;
    }

    const msrpStr = cols[3] ?? '0';
    const msrp = parseFloat(msrpStr.replace(/[$,]/g, ''));
    if (isNaN(msrp) || msrp < 0) {
      errors.push(`Row ${rowNum}: Invalid MSRP value "${msrpStr}"`);
      continue;
    }

    const applyProjectDiscounts = parseBooleanValue(cols[4]);

    rows.push({
      groupName: cols[0] || undefined,
      partNumber,
      description: cols[2] || undefined,
      msrp,
      applyProjectDiscounts,
    });
  }

  return { rows, errors };
}

export function CatalogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Product modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [prodGroupName, setProdGroupName] = useState('');
  const [prodPartNumber, setProdPartNumber] = useState('');
  const [prodDescription, setProdDescription] = useState('');
  const [prodMsrp, setProdMsrp] = useState('');
  const [prodApplyDiscount, setProdApplyDiscount] = useState(true);
  const [prodSaving, setProdSaving] = useState(false);
  const [prodError, setProdError] = useState('');

  // Bulk import modal
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkParsed, setBulkParsed] = useState<ParsedRow[]>([]);
  const [bulkErrors, setBulkErrors] = useState<string[]>([]);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkSuccess, setBulkSuccess] = useState('');

  // Delete modal
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Multi-select
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBatchDeleteModal, setShowBatchDeleteModal] = useState(false);
  const [batchDeleteLoading, setBatchDeleteLoading] = useState(false);

  const loadCatalog = useCallback(async () => {
    if (!id) return;
    try {
      const data = await fetchCatalog(id);
      setCatalog(data.catalog);
    } catch {
      // Catalog may have been deleted
    }
  }, [id]);

  const loadProducts = useCallback(
    async (search?: string, group?: string) => {
      if (!id) return;
      try {
        const data = await listProducts({
          catalogId: id,
          search: search || undefined,
          groupName: group || undefined,
        });
        setProducts(data.products);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Failed to load products');
      }
    },
    [id],
  );

  const loadGroups = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getProductGroups(id);
      setGroups(data.groups);
    } catch {
      // non-critical
    }
  }, [id]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadCatalog(), loadProducts(), loadGroups()]);
      setLoading(false);
    };
    init();
  }, [loadCatalog, loadProducts, loadGroups]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadProducts(searchTerm, groupFilter);
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchTerm, groupFilter, loadProducts]);

  const openAddProduct = () => {
    setEditingProduct(null);
    setProdGroupName('');
    setProdPartNumber('');
    setProdDescription('');
    setProdMsrp('');
    setProdApplyDiscount(true);
    setProdError('');
    setShowProductModal(true);
  };

  const openEditProduct = (product: Product) => {
    setEditingProduct(product);
    setProdGroupName(product.groupName ?? '');
    setProdPartNumber(product.partNumber);
    setProdDescription(product.description ?? '');
    setProdMsrp(product.msrp);
    setProdApplyDiscount(product.applyProjectDiscounts);
    setProdError('');
    setShowProductModal(true);
  };

  const handleSaveProduct = async (e: FormEvent) => {
    e.preventDefault();
    setProdSaving(true);
    setProdError('');

    const msrpNum = parseFloat(prodMsrp);
    if (isNaN(msrpNum) || msrpNum < 0) {
      setProdError('MSRP must be a valid number >= 0');
      setProdSaving(false);
      return;
    }

    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, {
          groupName: prodGroupName || null,
          partNumber: prodPartNumber,
          description: prodDescription || null,
          msrp: msrpNum,
          applyProjectDiscounts: prodApplyDiscount,
        });
      } else {
        await createProduct({
          catalogId: id!,
          groupName: prodGroupName || undefined,
          partNumber: prodPartNumber,
          description: prodDescription || undefined,
          msrp: msrpNum,
          applyProjectDiscounts: prodApplyDiscount,
        });
      }
      setShowProductModal(false);
      await Promise.all([loadProducts(searchTerm, groupFilter), loadGroups()]);
    } catch (err) {
      setProdError(err instanceof ApiError ? err.message : 'Failed to save product');
    } finally {
      setProdSaving(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!deletingProduct) return;
    setDeleteLoading(true);
    try {
      await deleteProduct(deletingProduct.id);
      setDeletingProduct(null);
      await loadProducts(searchTerm, groupFilter);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete product');
      setDeletingProduct(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleSelect = (productId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map((p) => p.id)));
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    setBatchDeleteLoading(true);
    try {
      await batchDeleteProducts([...selectedIds]);
      setSelectedIds(new Set());
      setShowBatchDeleteModal(false);
      await Promise.all([loadProducts(searchTerm, groupFilter), loadGroups()]);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete products');
      setShowBatchDeleteModal(false);
    } finally {
      setBatchDeleteLoading(false);
    }
  };

  // Bulk import handlers
  const handleBulkParse = () => {
    const { rows, errors } = parseImportData(bulkText);
    setBulkParsed(rows);
    setBulkErrors(errors);
    setBulkSuccess('');
  };

  const handleBulkImport = async () => {
    if (bulkParsed.length === 0) return;
    setBulkImporting(true);
    setBulkSuccess('');
    try {
      const result = await bulkCreateProducts({
        catalogId: id!,
        products: bulkParsed.map((r) => ({
          groupName: r.groupName,
          partNumber: r.partNumber,
          description: r.description,
          msrp: r.msrp,
          applyProjectDiscounts: r.applyProjectDiscounts,
        })),
      });
      setBulkSuccess(`Successfully imported ${result.count} products`);
      setBulkText('');
      setBulkParsed([]);
      setBulkErrors([]);
      await Promise.all([loadProducts(searchTerm, groupFilter), loadGroups()]);
    } catch (err) {
      setBulkErrors([err instanceof ApiError ? err.message : 'Import failed']);
    } finally {
      setBulkImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  const inputClasses =
    'w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500';

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <Link
        to="/catalogs"
        className="mb-4 inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Catalogs
      </Link>

      <div className="mb-6 flex items-center gap-3">
        <h1 className="text-2xl font-bold text-gray-100">{catalog?.name ?? 'Catalog'}</h1>
        {catalog && (
          <span
            className={`rounded-full px-2 py-0.5 text-xs ${
              catalog.active
                ? 'bg-green-900/30 text-green-400'
                : 'bg-gray-800 text-gray-500'
            }`}
          >
            {catalog.active ? 'Active' : 'Inactive'}
          </span>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">{error}</div>
      )}

      {/* Action Bar */}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          onClick={openAddProduct}
          className="flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          <Plus className="h-4 w-4" />
          Add Product
        </button>
        <button
          onClick={() => {
            setBulkText('');
            setBulkParsed([]);
            setBulkErrors([]);
            setBulkSuccess('');
            setShowBulkModal(true);
          }}
          className="flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-300 hover:bg-gray-700"
        >
          <Upload className="h-4 w-4" />
          Bulk Import
        </button>

        {selectedIds.size > 0 && (
          <button
            onClick={() => setShowBatchDeleteModal(true)}
            className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected ({selectedIds.size})
          </button>
        )}

        <div className="flex-1" />

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search products..."
            className="rounded-lg border border-gray-700 bg-gray-800 py-2 pl-9 pr-4 text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </div>

        {groups.length > 0 && (
          <select
            value={groupFilter}
            onChange={(e) => setGroupFilter(e.target.value)}
            className="rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            <option value="">All Groups</option>
            {groups.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Products Table */}
      {products.length === 0 ? (
        <div className="rounded-lg border border-gray-800 bg-gray-900 p-12 text-center">
          <p className="text-gray-500">
            {searchTerm || groupFilter
              ? 'No products match your filters.'
              : 'No products in this catalog. Add products or use bulk import.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-800">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-800/50 text-left text-xs uppercase tracking-wider text-gray-400">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={products.length > 0 && selectedIds.size === products.length}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-gray-700 bg-gray-800 text-brand-600 focus:ring-brand-500"
                  />
                </th>
                <th className="px-4 py-3">Group</th>
                <th className="px-4 py-3">Part Number</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3 text-right">MSRP</th>
                <th className="px-4 py-3 text-center">Proj. Disc.</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product) => (
                <tr
                  key={product.id}
                  className={`border-b border-gray-800 hover:bg-gray-800/30 ${selectedIds.has(product.id) ? 'bg-gray-800/20' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(product.id)}
                      onChange={() => toggleSelect(product.id)}
                      className="h-4 w-4 rounded border-gray-700 bg-gray-800 text-brand-600 focus:ring-brand-500"
                    />
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {product.groupName || '\u2014'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-100">
                    {product.partNumber}
                  </td>
                  <td className="max-w-xs truncate px-4 py-3 text-sm text-gray-300">
                    {product.description || '\u2014'}
                  </td>
                  <td className="px-4 py-3 text-right text-sm text-gray-100">
                    {formatCurrency(product.msrp)}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {product.applyProjectDiscounts ? (
                      <Check className="mx-auto h-4 w-4 text-green-400" />
                    ) : (
                      <X className="mx-auto h-4 w-4 text-gray-600" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => openEditProduct(product)}
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-gray-300"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeletingProduct(product)}
                        className="rounded p-1.5 text-gray-500 hover:bg-gray-800 hover:text-red-400"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-100">
              {editingProduct ? 'Edit Product' : 'Add Product'}
            </h2>

            {prodError && (
              <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">
                {prodError}
              </div>
            )}

            <form onSubmit={handleSaveProduct} className="space-y-4">
              <div>
                <label htmlFor="prod-group" className="mb-1 block text-sm text-gray-400">
                  Group Name
                </label>
                <input
                  id="prod-group"
                  type="text"
                  value={prodGroupName}
                  onChange={(e) => setProdGroupName(e.target.value)}
                  className={inputClasses}
                  placeholder="e.g. Managed Switches"
                  list="group-suggestions"
                />
                <datalist id="group-suggestions">
                  {groups.map((g) => (
                    <option key={g} value={g} />
                  ))}
                </datalist>
              </div>

              <div>
                <label htmlFor="prod-pn" className="mb-1 block text-sm text-gray-400">
                  Part Number *
                </label>
                <input
                  id="prod-pn"
                  type="text"
                  value={prodPartNumber}
                  onChange={(e) => setProdPartNumber(e.target.value)}
                  className={inputClasses}
                  placeholder="e.g. SW-24G-POE"
                  required
                />
              </div>

              <div>
                <label htmlFor="prod-desc" className="mb-1 block text-sm text-gray-400">
                  Description
                </label>
                <textarea
                  id="prod-desc"
                  value={prodDescription}
                  onChange={(e) => setProdDescription(e.target.value)}
                  rows={2}
                  className={inputClasses}
                  placeholder="Product description..."
                />
              </div>

              <div>
                <label htmlFor="prod-msrp" className="mb-1 block text-sm text-gray-400">
                  MSRP *
                </label>
                <input
                  id="prod-msrp"
                  type="number"
                  step="0.01"
                  min="0"
                  value={prodMsrp}
                  onChange={(e) => setProdMsrp(e.target.value)}
                  className={inputClasses}
                  placeholder="0.00"
                  required
                />
              </div>

              <label className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={prodApplyDiscount}
                  onChange={(e) => setProdApplyDiscount(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-700 bg-gray-800 text-brand-600 focus:ring-brand-500"
                />
                <span className="text-sm text-gray-300">Apply Project Discounts</span>
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowProductModal(false)}
                  className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={prodSaving}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {prodSaving ? 'Saving...' : editingProduct ? 'Save Changes' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-5xl rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-100">Bulk Import Products</h2>
            <p className="mb-4 text-sm text-gray-400">
              Paste tab-separated or CSV data with columns:{' '}
              <span className="font-medium text-gray-300">
                Group, Part Number, Description, MSRP, Apply Project Discounts
              </span>{' '}
              (true/false). Header row is optional and will be auto-detected.
            </p>

            {bulkSuccess && (
              <div className="mb-4 rounded-lg bg-green-900/30 px-4 py-3 text-sm text-green-400">
                {bulkSuccess}
              </div>
            )}

            {bulkErrors.length > 0 && (
              <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">
                {bulkErrors.map((err, i) => (
                  <div key={i}>{err}</div>
                ))}
              </div>
            )}

            <textarea
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              rows={8}
              className="mb-3 w-full rounded-lg border border-gray-700 bg-gray-800 px-4 py-2.5 font-mono text-sm text-gray-100 placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder={`Group\tPart Number\tDescription\tMSRP\tApply Project Discounts\nSwitches\tSW-24G\t24-Port Gigabit Switch\t599.99\tYes\nSoftware\tSW-LIC-01\tBase License\t1299.99\tNo`}
            />

            <button
              onClick={handleBulkParse}
              disabled={!bulkText.trim()}
              className="mb-4 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 disabled:opacity-50"
            >
              Preview Import
            </button>

            {bulkParsed.length > 0 && (
              <div className="mb-4 max-h-60 overflow-auto rounded-lg border border-gray-800">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-800/50 text-left text-xs uppercase tracking-wider text-gray-400">
                      <th className="px-3 py-2">Group</th>
                      <th className="px-3 py-2">Part #</th>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2 text-right">MSRP</th>
                      <th className="px-3 py-2 text-center">Proj. Disc.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkParsed.map((row, i) => (
                      <tr key={i} className="border-b border-gray-800">
                        <td className="px-3 py-2 text-gray-400">{row.groupName || '\u2014'}</td>
                        <td className="px-3 py-2 text-gray-100">{row.partNumber}</td>
                        <td className="max-w-[200px] truncate px-3 py-2 text-gray-300">
                          {row.description || '\u2014'}
                        </td>
                        <td className="px-3 py-2 text-right text-gray-100">
                          {formatCurrency(row.msrp)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {row.applyProjectDiscounts ? (
                            <Check className="mx-auto h-3 w-3 text-green-400" />
                          ) : (
                            <X className="mx-auto h-3 w-3 text-gray-600" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBulkModal(false)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              {bulkParsed.length > 0 && (
                <button
                  onClick={handleBulkImport}
                  disabled={bulkImporting}
                  className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {bulkImporting
                    ? 'Importing...'
                    : `Import ${bulkParsed.length} Product${bulkParsed.length !== 1 ? 's' : ''}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Product Confirmation */}
      {deletingProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-100">Delete Product</h2>
            <p className="mb-6 text-sm text-gray-400">
              Delete product &ldquo;{deletingProduct.partNumber}&rdquo;? This action cannot be
              undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeletingProduct(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteProduct}
                disabled={deleteLoading}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Delete Confirmation */}
      {showBatchDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-100">Delete Products</h2>
            <p className="mb-6 text-sm text-gray-400">
              Delete {selectedIds.size} product{selectedIds.size !== 1 ? 's' : ''}? This action
              cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBatchDeleteModal(false)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleBatchDelete}
                disabled={batchDeleteLoading}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {batchDeleteLoading ? 'Deleting...' : `Delete ${selectedIds.size}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
